import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FileUpload } from 'primeng/fileupload';

import { TipoAviso } from '../../enums/TipoAviso.enum';
import {
  AvisoDTO,
  AvisoFormDTO,
} from '../../models/avisos/aviso.model';
import { AvisoService } from '../../services/avisos/aviso.service';
import { TableColumn } from '../../models/shared/table-column.model';
import { environment } from '../../environments/environment';
import { ImagemUtils } from '../../shared/utils/imagem-utils';

@Component({
  selector: 'app-avisos',
  templateUrl: './avisos.component.html',
  styleUrl: './avisos.component.scss',
  providers: [MessageService],
})
export class AvisosComponent implements OnInit, OnDestroy {
  // =========================================================================
  // Estado da listagem
  // =========================================================================

  avisos: AvisoDTO[] = [];
  first = 0;
  rows = 10;
  private _lastRows = 10;
  totalRecords = 0;
  loading = true;

  searchDescricao = '';
  filtroTipo: { label: string; value: TipoAviso } | undefined;
  filtroAtivo: { label: string; value: boolean | null } | undefined;

  lastfield = 'id';
  lastSort = 'desc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  // =========================================================================
  // Estado do formulario
  // =========================================================================

  form: AvisoFormDTO = this.createEmptyForm();
  isFormSubmitted = false;
  submitting = false;
  activeTabIndex = 1;

  // Upload / preview da imagem
  imageBase64: string | null = null;
  imagePreviewUrl: string | null = null;
  fileName: string | null = null;

  @ViewChild('fileUpload') fileUploadRef!: FileUpload;
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('deleteConfirmBtn') deleteConfirmBtnRef!: ElementRef<HTMLButtonElement>;

  // =========================================================================
  // Dialog de inativacao
  // =========================================================================

  deleteDialogVisible = false;
  avisoSelecionadoParaInativar: AvisoDTO | null = null;

  // =========================================================================
  // Opcoes de dropdowns
  // =========================================================================

  tipoOptions: { label: string; value: TipoAviso }[] = [];
  filtroAtivoOptions: { label: string; value: boolean | null }[] = [];

  // =========================================================================
  // Colunas da tabela (persistencia simples)
  // =========================================================================

  readonly STORAGE_KEY = 'avisos-visible-columns';

  columns: TableColumn[] = [
    { key: 'titulo', labelKey: 'avisos.labels.titulo', visible: true, fixed: true, defaultVisible: true },
    { key: 'tipoAviso', labelKey: 'avisos.labels.tipo_aviso', visible: true, fixed: false, defaultVisible: true },
    { key: 'dataInicial', labelKey: 'avisos.labels.data_inicial', visible: true, fixed: false, defaultVisible: true },
    { key: 'dataFinal', labelKey: 'avisos.labels.data_final', visible: true, fixed: false, defaultVisible: true },
    { key: 'ativo', labelKey: 'avisos.labels.ativo', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'avisos.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  // =========================================================================
  // Traducoes pre-carregadas
  // =========================================================================

  texts = {
    titulo: '',
    subtitulo: '',
    add_novo_aviso: '',
    lista_de_avisos: '',
    confirmar_inativacao: '',
    confirmacao: '',
    nenhum_aviso: '',
    ajuste_filtros: '',
    carregando: '',
    erro: '',
    sucesso: '',
    descricao_obrigatoria: '',
    descricao_muito_longa: '',
    titulo_obrigatorio: '',
    data_inicial_obrigatoria: '',
    tipo_obrigatorio: '',
    imagem_obrigatoria: '',
    url_video_obrigatoria: '',
    url_link_obrigatoria: '',
    url_link_invalida: '',
    texto_dialogo_obrigatorio: '',
    numero_whatsapp_obrigatorio: '',
    texto_chat_whatsapp_obrigatorio: '',
    perfil_instagram_obrigatorio: '',
    arquivo_invalido: '',
    aviso_adicionado_sucesso: '',
    aviso_editado_sucesso: '',
    erro_ao_salvar_aviso: '',
    aviso_inativado_sucesso: '',
    erro_ao_inativar_aviso: '',
    pre_visualizacao: '',
    aba_cadastro: '',
    aba_lista: '',
    todos: '',
    sim: '',
    nao: '',
    apenas_ativos: '',
    apenas_inativos: '',
  };

  labels = {
    titulo_aviso: '',
    descricao: '',
    tipo_aviso: '',
    data_inicial: '',
    data_final: '',
    imagem: '',
    url_video: '',
    url_link: '',
    texto_dialogo: '',
    numero_whatsapp: '',
    texto_chat_whatsapp: '',
    perfil_instagram: '',
    texto_chat_instagram: '',
    ativo: '',
    acoes: '',
    itens_por_pagina: '',
    filtro_tipo: '',
    filtro_situacao: '',
  };

  placeholders = {
    titulo: '',
    descricao: '',
    tipo_aviso: '',
    data_inicial: '',
    data_final: '',
    url_video: '',
    url_link: '',
    texto_dialogo: '',
    numero_whatsapp: '',
    texto_chat_whatsapp: '',
    perfil_instagram: '',
    texto_chat_instagram: '',
    pesquisar: '',
    filtro_situacao: '',
  };

  botoes = {
    adicionar: '',
    selecionar_imagem: '',
    remover: '',
    salvar: '',
    limpar: '',
    novo_aviso: '',
    confirmar: '',
    cancelar: '',
    inativar: '',
  };

  tooltips = {
    editar: '',
    inativar: '',
    ver_imagem: '',
  };

  // Dialog de imagem expandida
  displayDialogImagem = false;
  imagemExpandidaUrl = '';

  private langChangeSub!: Subscription;

  // Debounce da busca por descricao
  private buscaDescricao$ = new Subject<string>();
  private buscaDescricaoSub?: Subscription;

  constructor(
    private messageService: MessageService,
    private avisoService: AvisoService,
    private translate: TranslateService
  ) {}

  // =========================================================================
  // Ciclo de vida
  // =========================================================================

  ngOnInit(): void {
    this.loadColumnPreferences();
    this.loadTranslations();
    this.buildDropdownOptions();
    this.setupBuscaDescricaoDebounce();
    this.buscarAvisos();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildDropdownOptions();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    if (this.buscaDescricaoSub) {
      this.buscaDescricaoSub.unsubscribe();
    }
  }

  // =========================================================================
  // Filtros automaticos (debounce no input + onChange nos dropdowns)
  // =========================================================================

  /** Aplica o filtro de descricao com debounce de 400ms para evitar disparar requisicao a cada tecla. */
  private setupBuscaDescricaoDebounce(): void {
    this.buscaDescricaoSub = this.buscaDescricao$
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => this.aplicarFiltros());
  }

  /** Chamado pelo input de busca a cada tecla pressionada. */
  onBuscaDescricaoChange(): void {
    this.buscaDescricao$.next(this.searchDescricao ?? '');
  }

  // =========================================================================
  // Traducoes
  // =========================================================================

  onTabChange(event: { index: number }): void {
    this.activeTabIndex = event.index;
    this.loadTranslations();
  }

  private loadTranslations(): void {
    const isEdicao = !!this.form.id;
    const isListaAtiva = this.activeTabIndex === 1;

    this.texts = {
      titulo: this.translate.instant(
        isListaAtiva
          ? 'avisos.texts.titulo_listagem'
          : isEdicao
            ? 'avisos.texts.titulo_editar'
            : 'avisos.texts.titulo_criar'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva
          ? 'avisos.texts.subtitulo_listagem'
          : isEdicao
            ? 'avisos.texts.subtitulo_editar'
            : 'avisos.texts.subtitulo_criar'
      ),
      add_novo_aviso: this.translate.instant('avisos.texts.add_novo_aviso'),
      lista_de_avisos: this.translate.instant('avisos.texts.lista_de_avisos'),
      confirmar_inativacao: this.translate.instant('avisos.texts.confirmar_inativacao'),
      confirmacao: this.translate.instant('avisos.texts.confirmacao'),
      nenhum_aviso: this.translate.instant('avisos.texts.nenhum_aviso'),
      ajuste_filtros: this.translate.instant('avisos.texts.ajuste_filtros'),
      carregando: this.translate.instant('avisos.texts.carregando'),
      erro: this.translate.instant('avisos.texts.erro'),
      sucesso: this.translate.instant('avisos.texts.sucesso'),
      descricao_obrigatoria: this.translate.instant('avisos.texts.descricao_obrigatoria'),
      descricao_muito_longa: this.translate.instant('avisos.texts.descricao_muito_longa'),
      titulo_obrigatorio: this.translate.instant('avisos.texts.titulo_obrigatorio'),
      data_inicial_obrigatoria: this.translate.instant('avisos.texts.data_inicial_obrigatoria'),
      tipo_obrigatorio: this.translate.instant('avisos.texts.tipo_obrigatorio'),
      imagem_obrigatoria: this.translate.instant('avisos.texts.imagem_obrigatoria'),
      url_video_obrigatoria: this.translate.instant('avisos.texts.url_video_obrigatoria'),
      url_link_obrigatoria: this.translate.instant('avisos.texts.url_link_obrigatoria'),
      url_link_invalida: this.translate.instant('avisos.texts.url_link_invalida'),
      texto_dialogo_obrigatorio: this.translate.instant('avisos.texts.texto_dialogo_obrigatorio'),
      numero_whatsapp_obrigatorio: this.translate.instant('avisos.texts.numero_whatsapp_obrigatorio'),
      texto_chat_whatsapp_obrigatorio: this.translate.instant('avisos.texts.texto_chat_whatsapp_obrigatorio'),
      perfil_instagram_obrigatorio: this.translate.instant('avisos.texts.perfil_instagram_obrigatorio'),
      arquivo_invalido: this.translate.instant('avisos.texts.arquivo_invalido'),
      aviso_adicionado_sucesso: this.translate.instant('avisos.texts.aviso_adicionado_sucesso'),
      aviso_editado_sucesso: this.translate.instant('avisos.texts.aviso_editado_sucesso'),
      erro_ao_salvar_aviso: this.translate.instant('avisos.texts.erro_ao_salvar_aviso'),
      aviso_inativado_sucesso: this.translate.instant('avisos.texts.aviso_inativado_sucesso'),
      erro_ao_inativar_aviso: this.translate.instant('avisos.texts.erro_ao_inativar_aviso'),
      pre_visualizacao: this.translate.instant('avisos.texts.pre_visualizacao'),
      aba_cadastro: this.translate.instant('avisos.texts.aba_cadastro'),
      aba_lista: this.translate.instant('avisos.texts.aba_lista'),
      todos: this.translate.instant('avisos.texts.todos'),
      sim: this.translate.instant('avisos.texts.sim'),
      nao: this.translate.instant('avisos.texts.nao'),
      apenas_ativos: this.translate.instant('avisos.texts.apenas_ativos'),
      apenas_inativos: this.translate.instant('avisos.texts.apenas_inativos'),
    };

    this.labels = {
      titulo_aviso: this.translate.instant('avisos.labels.titulo_aviso'),
      descricao: this.translate.instant('avisos.labels.descricao'),
      tipo_aviso: this.translate.instant('avisos.labels.tipo_aviso'),
      data_inicial: this.translate.instant('avisos.labels.data_inicial'),
      data_final: this.translate.instant('avisos.labels.data_final'),
      imagem: this.translate.instant('avisos.labels.imagem'),
      url_video: this.translate.instant('avisos.labels.url_video'),
      url_link: this.translate.instant('avisos.labels.url_link'),
      texto_dialogo: this.translate.instant('avisos.labels.texto_dialogo'),
      numero_whatsapp: this.translate.instant('avisos.labels.numero_whatsapp'),
      texto_chat_whatsapp: this.translate.instant('avisos.labels.texto_chat_whatsapp'),
      perfil_instagram: this.translate.instant('avisos.labels.perfil_instagram'),
      texto_chat_instagram: this.translate.instant('avisos.labels.texto_chat_instagram'),
      ativo: this.translate.instant('avisos.labels.ativo'),
      acoes: this.translate.instant('avisos.labels.acoes'),
      itens_por_pagina: this.translate.instant('avisos.labels.itens_por_pagina'),
      filtro_tipo: this.translate.instant('avisos.labels.filtro_tipo'),
      filtro_situacao: this.translate.instant('avisos.labels.filtro_situacao'),
    };

    this.placeholders = {
      titulo: this.translate.instant('avisos.placeholders.titulo'),
      descricao: this.translate.instant('avisos.placeholders.descricao'),
      tipo_aviso: this.translate.instant('avisos.placeholders.tipo_aviso'),
      data_inicial: this.translate.instant('avisos.placeholders.data_inicial'),
      data_final: this.translate.instant('avisos.placeholders.data_final'),
      url_video: this.translate.instant('avisos.placeholders.url_video'),
      url_link: this.translate.instant('avisos.placeholders.url_link'),
      texto_dialogo: this.translate.instant('avisos.placeholders.texto_dialogo'),
      numero_whatsapp: this.translate.instant('avisos.placeholders.numero_whatsapp'),
      texto_chat_whatsapp: this.translate.instant('avisos.placeholders.texto_chat_whatsapp'),
      perfil_instagram: this.translate.instant('avisos.placeholders.perfil_instagram'),
      texto_chat_instagram: this.translate.instant('avisos.placeholders.texto_chat_instagram'),
      pesquisar: this.translate.instant('avisos.placeholders.pesquisar'),
      filtro_situacao: this.translate.instant('avisos.placeholders.filtro_situacao'),
    };

    this.botoes = {
      adicionar: this.translate.instant('avisos.botoes.adicionar'),
      selecionar_imagem: this.translate.instant('avisos.botoes.selecionar_imagem'),
      remover: this.translate.instant('avisos.botoes.remover'),
      salvar: this.translate.instant('avisos.botoes.salvar'),
      limpar: this.translate.instant('avisos.botoes.limpar'),
      novo_aviso: this.translate.instant('avisos.botoes.novo_aviso'),
      confirmar: this.translate.instant('avisos.botoes.confirmar'),
      cancelar: this.translate.instant('avisos.botoes.cancelar'),
      inativar: this.translate.instant('avisos.botoes.inativar'),
    };

    this.tooltips = {
      editar: this.translate.instant('avisos.tooltips.editar'),
      inativar: this.translate.instant('avisos.tooltips.inativar'),
      ver_imagem: this.translate.instant('avisos.tooltips.ver_imagem'),
    };
  }

  // =========================================================================
  // Dropdown options
  // =========================================================================

  private buildDropdownOptions(): void {
    this.tipoOptions = Object.values(TipoAviso).map((t) => ({
      value: t,
      label: this.translate.instant(`avisos.tipos.${t.toLowerCase()}`),
    }));

    this.filtroAtivoOptions = [
      { label: this.translate.instant('avisos.texts.todos'), value: null },
      { label: this.translate.instant('avisos.texts.apenas_ativos'), value: true },
      { label: this.translate.instant('avisos.texts.apenas_inativos'), value: false },
    ];
  }

  // =========================================================================
  // Helpers de tipo (mostra/esconde campos por tipo)
  // =========================================================================

  get tipoSelecionado(): TipoAviso | undefined {
    return this.form.tipoAviso?.value;
  }

  get isTipoVideo(): boolean {
    return this.tipoSelecionado === TipoAviso.VIDEO || this.tipoSelecionado === TipoAviso.TUTORIAL;
  }

  get isTipoLink(): boolean {
    return this.tipoSelecionado === TipoAviso.LINK;
  }

  get isTipoDialogo(): boolean {
    return this.tipoSelecionado === TipoAviso.DIALOGO;
  }

  get isTipoWhatsapp(): boolean {
    return this.tipoSelecionado === TipoAviso.CHAT_WHATSAPP;
  }

  get isTipoInstagram(): boolean {
    return this.tipoSelecionado === TipoAviso.PERFIL_INSTAGRAM;
  }

  // =========================================================================
  // Upload de imagem
  // =========================================================================

  async onSelectImage(event: any): Promise<void> {
    const file: File | undefined = event.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.messageService.add({
        severity: 'error',
        summary: this.texts.erro,
        detail: this.texts.arquivo_invalido,
      });
      return;
    }

    this.fileName = file.name;
    const imagem = await ImagemUtils.comprimirParaBase64(file);
    this.imagePreviewUrl = imagem.dataUrl;
    this.imageBase64 = imagem.base64;
  }

  onClearImage(): void {
    this.fileName = null;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    if (this.fileUploadRef) {
      this.fileUploadRef.clear();
    }
  }

  /** Verifica se a string e uma URL textual valida (http ou https). Rejeita base64 e strings invalidas. */
  private isUrlValida(url: string): boolean {
    const trimmed = url.trim();
    if (trimmed.startsWith('data:')) {
      return false;
    }
    try {
      const parsed = new URL(trimmed);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  // =========================================================================
  // Listagem (tabela)
  // =========================================================================

  buscarAvisos(
    page: number = 0,
    size: number = this.rows,
    sort: string = `${this.lastfield},${this.lastSort}`
  ): void {
    this.loading = true;

    const params: any = { page, size, sort };

    // Filtros opcionais (so envia se houver valor)
    if (this.searchDescricao && this.searchDescricao.trim().length > 0) {
      params.descricao = this.searchDescricao.trim();
    }
    if (this.filtroTipo?.value) {
      params.tipo = this.filtroTipo.value;
    }

    // Decide endpoint conforme filtro de ativo
    const usarFiltroAtivo =
      this.filtroAtivo !== undefined && this.filtroAtivo.value !== null;

    const url = usarFiltroAtivo ? '/avisos/filtro-ativo' : '/avisos';

    if (usarFiltroAtivo) {
      params.ativo = this.filtroAtivo!.value;
      if (this.filtroTipo?.value) {
        params.tipoAviso = this.filtroTipo.value;
        delete params.tipo;
      }
    }

    this.avisoService.get(url, params).subscribe({
      next: (response) => {
        this.avisos = response.content || [];
        this.totalRecords = response.totalElements ?? 0;
        this.first = page * size;
        this.loading = false;
      },
      error: () => {
        this.avisos = [];
        this.totalRecords = 0;
        this.loading = false;
      },
    });
  }

  aplicarFiltros(): void {
    this.first = 0;
    this.buscarAvisos(0, this.rows);
  }

  limparFiltros(): void {
    this.searchDescricao = '';
    this.filtroTipo = undefined;
    this.filtroAtivo = undefined;
    this.aplicarFiltros();
  }

  onPageChange(event: any): void {
    const page = event.page;
    const size = event.rows;
    this.rows = size;
    this.buscarAvisos(page, size, `${this.lastfield},${this.lastSort}`);
  }

  onRowsPerPageChange(): void {
    if (this.rows === this._lastRows) {
      return;
    }
    this._lastRows = this.rows;
    this.first = 0;
    this.buscarAvisos(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  /**
   * Ordenacao server-side.
   *
   * O `<p-table>` esta com [customSort]="true", entao a tabela NAO ordena em memoria.
   * Aqui apenas capturamos o campo + direcao, gravamos em `lastfield`/`lastSort` e
   * disparamos `buscarAvisos` (reset para a primeira pagina). O backend devolve
   * a pagina ja ordenada via `?sort=campo,asc|desc` (Spring Pageable).
   */
  customSort(event: SortEvent): void {
    if (!event.field) {
      return;
    }

    const field = event.field;
    const order = event.order ?? 1;
    const direction = order === 1 ? 'asc' : 'desc';

    // Se nada mudou em relacao a ultima ordenacao, evita disparar requisicao duplicada.
    // O (sortFunction) e chamado tanto no clique do usuario quanto apos o set do `value`
    // da tabela; sem este guard, cada `buscarAvisos` provocaria um re-disparo.
    if (this.lastfield === field && this.lastSort === direction) {
      return;
    }

    this.lastfield = field;
    this.lastSort = direction;

    // Reset para a primeira pagina ao trocar ordenacao
    this.first = 0;
    this.buscarAvisos(0, this.rows, `${field},${direction}`);
  }

  // =========================================================================
  // Formatacao
  // =========================================================================

  formatarDataEHora(dataISO: string | null | undefined): string {
    if (!dataISO) return '';
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  labelTipo(tipo: TipoAviso | string | undefined): string {
    if (!tipo) return '';
    return this.translate.instant(`avisos.tipos.${String(tipo).toLowerCase()}`);
  }

  // =========================================================================
  // Imagem na tabela
  // =========================================================================

  avisoTemImagem(aviso: AvisoDTO): boolean {
    return !!aviso.imagemBanner && String(aviso.imagemBanner).trim().length > 0;
  }

  abrirDialogImagem(imagem: string): void {
    if (!imagem) return;
    if (imagem.startsWith('http') || imagem.startsWith('data:image')) {
      this.imagemExpandidaUrl = imagem;
    } else {
      // Backend retorna apenas o nome do arquivo; monta URL do servidor de imagens
      this.imagemExpandidaUrl = `${environment.imgHost}/aviso/${imagem}`;
    }
    this.displayDialogImagem = true;
  }

  onImagemExpandidaError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  // =========================================================================
  // Submit (criar/atualizar)
  // =========================================================================

  submit(): void {
    if (this.submitting) return;
    this.isFormSubmitted = true;

    if (!this.validarFormulario()) {
      return;
    }

    const body = this.montarPayload();

    this.submitting = true;

    if (this.form.id) {
      // Atualizar
      this.avisoService.put(`/avisos/${this.form.id}`, body).subscribe({
        next: () => {
          this.submitting = false;
          this.messageService.add({
            severity: 'success',
            summary: this.texts.sucesso,
            detail: this.texts.aviso_editado_sucesso,
          });
          this.limparForm();
          this.buscarAvisos();
          this.activeTabIndex = 1;
          this.loadTranslations();
        },
        error: (err) => {
          this.submitting = false;
          this.messageService.add({
            severity: 'error',
            summary: this.texts.erro,
            detail: err?.error?.message || this.texts.erro_ao_salvar_aviso,
          });
        },
      });
    } else {
      // Criar
      this.avisoService.post('/avisos', body).subscribe({
        next: () => {
          this.submitting = false;
          this.messageService.add({
            severity: 'success',
            summary: this.texts.sucesso,
            detail: this.texts.aviso_adicionado_sucesso,
          });
          this.limparForm();
          this.buscarAvisos();
          this.activeTabIndex = 1;
          this.loadTranslations();
        },
        error: (err) => {
          this.submitting = false;
          this.messageService.add({
            severity: 'error',
            summary: this.texts.erro,
            detail: err?.error?.message || this.texts.erro_ao_salvar_aviso,
          });
        },
      });
    }
  }

  /**
   * Valida o formulario aplicando as mesmas regras do backend
   * (AvisoService.validarCamposTipoSelecionado) para evitar 422.
   */
  private validarFormulario(): boolean {
    if (!this.form.titulo || this.form.titulo.trim().length === 0) {
      this.toastErro(this.texts.titulo_obrigatorio);
      return false;
    }
    if (!this.form.descricao || this.form.descricao.trim().length === 0) {
      this.toastErro(this.texts.descricao_obrigatoria);
      return false;
    }
    if (this.form.descricao.length > 255) {
      this.toastErro(this.texts.descricao_muito_longa);
      return false;
    }
    if (!this.form.dataInicial) {
      this.toastErro(this.texts.data_inicial_obrigatoria);
      return false;
    }
    if (!this.form.tipoAviso?.value) {
      this.toastErro(this.texts.tipo_obrigatorio);
      return false;
    }

    // Imagem obrigatoria apenas na criacao (backend permite manter atual na edicao)
    if (!this.form.id && !this.imageBase64) {
      this.toastErro(this.texts.imagem_obrigatoria);
      return false;
    }

    const tipo = this.form.tipoAviso.value;

    if ((tipo === TipoAviso.VIDEO || tipo === TipoAviso.TUTORIAL) && !this.form.urlVideo) {
      this.toastErro(this.texts.url_video_obrigatoria);
      return false;
    }
    if (tipo === TipoAviso.LINK && !this.form.urlLink) {
      this.toastErro(this.texts.url_link_obrigatoria);
      return false;
    }
    if (tipo === TipoAviso.LINK && this.form.urlLink && !this.isUrlValida(this.form.urlLink)) {
      this.toastErro(this.texts.url_link_invalida);
      return false;
    }
    if (tipo === TipoAviso.DIALOGO && !this.form.textoDialogo) {
      this.toastErro(this.texts.texto_dialogo_obrigatorio);
      return false;
    }
    if (tipo === TipoAviso.CHAT_WHATSAPP) {
      if (!this.form.numeroWhatsapp) {
        this.toastErro(this.texts.numero_whatsapp_obrigatorio);
        return false;
      }
      if (!this.form.textoChatWhatsapp) {
        this.toastErro(this.texts.texto_chat_whatsapp_obrigatorio);
        return false;
      }
    }
    if (tipo === TipoAviso.PERFIL_INSTAGRAM && !this.form.perfilInstagram) {
      this.toastErro(this.texts.perfil_instagram_obrigatorio);
      return false;
    }

    return true;
  }

  /** Monta o payload `AvisoDTO` a partir do FormDTO. */
  private montarPayload(): AvisoDTO {
    return {
      ativo: this.form.ativo,
      titulo: this.form.titulo.trim(),
      descricao: this.form.descricao.trim(),
      dataInicial: this.formatarDataISO(this.form.dataInicial!),
      dataFinal: this.form.dataFinal ? this.formatarDataISO(this.form.dataFinal) : null,
      tipoAviso: this.form.tipoAviso!.value,
      imagemBanner: this.imageBase64 || null,
      urlVideo: this.form.urlVideo || null,
      urlLink: this.form.urlLink || null,
      textoDialogo: this.form.textoDialogo || null,
      numeroWhatsapp: this.form.numeroWhatsapp || null,
      textoChatWhatsapp: this.form.textoChatWhatsapp || null,
      perfilInstagram: this.form.perfilInstagram || null,
      textoChatInstagram: this.form.textoChatInstagram || null,
    };
  }

  /** Converte Date local para string no formato esperado pelo backend (yyyy-MM-ddTHH:mm:ss). */
  private formatarDataISO(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    const ano = d.getFullYear();
    const mes = pad(d.getMonth() + 1);
    const dia = pad(d.getDate());
    const hora = pad(d.getHours());
    const min = pad(d.getMinutes());
    const seg = pad(d.getSeconds());
    return `${ano}-${mes}-${dia}T${hora}:${min}:${seg}`;
  }

  private toastErro(detalhe: string): void {
    this.messageService.add({
      severity: 'error',
      summary: this.texts.erro,
      detail: detalhe,
    });
  }

  // =========================================================================
  // Edicao / Novo
  // =========================================================================

  editar(aviso: AvisoDTO): void {
    this.form = {
      id: aviso.id,
      ativo: aviso.ativo,
      titulo: aviso.titulo,
      descricao: aviso.descricao,
      dataInicial: aviso.dataInicial ? new Date(aviso.dataInicial) : null,
      dataFinal: aviso.dataFinal ? new Date(aviso.dataFinal) : null,
      tipoAviso: this.tipoOptions.find((t) => t.value === aviso.tipoAviso),
      urlVideo: aviso.urlVideo ?? null,
      urlLink: aviso.urlLink ?? null,
      textoDialogo: aviso.textoDialogo ?? null,
      numeroWhatsapp: aviso.numeroWhatsapp ?? null,
      textoChatWhatsapp: aviso.textoChatWhatsapp ?? null,
      perfilInstagram: aviso.perfilInstagram ?? null,
      textoChatInstagram: aviso.textoChatInstagram ?? null,
    };

    // Mostra a imagem atual no preview (se houver) sem enviar novamente
    if (aviso.imagemBanner) {
      this.imagePreviewUrl = aviso.imagemBanner.startsWith('http') || aviso.imagemBanner.startsWith('data:image')
        ? aviso.imagemBanner
        : `${environment.imgHost}/aviso/${aviso.imagemBanner}`;
      this.imageBase64 = null; // so envia se o usuario trocar
      this.fileName = null;
    } else {
      this.imagePreviewUrl = null;
      this.imageBase64 = null;
      this.fileName = null;
    }

    this.activeTabIndex = 0;
    this.loadTranslations();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  novoAviso(): void {
    this.limparForm();
    this.activeTabIndex = 0;
    this.loadTranslations();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  limparForm(): void {
    this.form = this.createEmptyForm();
    this.onClearImage();
    this.isFormSubmitted = false;
  }

  createEmptyForm(): AvisoFormDTO {
    return {
      ativo: true,
      titulo: '',
      descricao: '',
      dataInicial: null,
      dataFinal: null,
      tipoAviso: undefined,
      urlVideo: null,
      urlLink: null,
      textoDialogo: null,
      numeroWhatsapp: null,
      textoChatWhatsapp: null,
      perfilInstagram: null,
      textoChatInstagram: null,
    };
  }

  // =========================================================================
  // Inativacao (substitui DELETE - o backend nao expoe DELETE para avisos)
  // =========================================================================

  abrirDialogInativacao(aviso: AvisoDTO): void {
    this.avisoSelecionadoParaInativar = aviso;
    this.deleteDialogVisible = true;
  }

  onDeleteDialogShow(): void {
    setTimeout(() => {
      this.deleteConfirmBtnRef?.nativeElement?.focus();
    }, 50);
  }

  confirmarInativacao(): void {
    if (!this.avisoSelecionadoParaInativar?.id) {
      this.deleteDialogVisible = false;
      return;
    }

    // Monta payload espelhando o aviso atual, marcando ativo = false
    const aviso = this.avisoSelecionadoParaInativar;
    const body: AvisoDTO = {
      ativo: false,
      titulo: aviso.titulo,
      descricao: aviso.descricao,
      dataInicial: aviso.dataInicial,
      dataFinal: aviso.dataFinal ?? null,
      tipoAviso: aviso.tipoAviso,
      imagemBanner: null, // null => backend mantem a imagem atual na edicao
      urlVideo: aviso.urlVideo ?? null,
      urlLink: aviso.urlLink ?? null,
      textoDialogo: aviso.textoDialogo ?? null,
      numeroWhatsapp: aviso.numeroWhatsapp ?? null,
      textoChatWhatsapp: aviso.textoChatWhatsapp ?? null,
      perfilInstagram: aviso.perfilInstagram ?? null,
      textoChatInstagram: aviso.textoChatInstagram ?? null,
    };

    this.avisoService.put(`/avisos/${aviso.id}`, body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.sucesso,
          detail: this.texts.aviso_inativado_sucesso,
        });
        this.deleteDialogVisible = false;
        this.avisoSelecionadoParaInativar = null;
        this.buscarAvisos();
      },
      error: (err) => {
        this.deleteDialogVisible = false;
        this.avisoSelecionadoParaInativar = null;
        this.messageService.add({
          severity: 'error',
          summary: this.texts.erro,
          detail: err?.error?.message || this.texts.erro_ao_inativar_aviso,
        });
      },
    });
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find((c) => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter((c) => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach((col) => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch {
      // Silenciosamente ignora — usa defaults
    }
  }

  // =========================================================================
  // Atalhos de teclado
  // =========================================================================

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent): void {
    if (this.deleteDialogVisible) {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this.confirmarInativacao();
        return;
      }
      if (ev.key === 'Escape') {
        ev.preventDefault();
        this.deleteDialogVisible = false;
        return;
      }
      return;
    }

    if (ev.key === 'F2') {
      ev.preventDefault();
      setTimeout(() => {
        this.searchInputRef?.nativeElement?.focus();
      }, 100);
      return;
    }

    if (ev.ctrlKey && ev.key === 'Enter' && this.activeTabIndex === 0) {
      ev.preventDefault();
      this.submit();
      return;
    }
  }
}
